import type { ReactNode } from 'react';
import styled from 'styled-components';

import {
  Horizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex/index.tsx';
import { LegalWarningBanner } from '@sorare/core/src/components/jonum/LegalWarningBanner/index.tsx';
import { AbsoluteNav } from '@sorare/core/src/components/navigation/AppNavigation/index.tsx';
import {
  desktopAndAbove,
  laptopAndAbove,
} from '@sorare/core/src/style/mediaQuery';

const Main = styled(Horizontal)`
  position: relative;
  height: calc(var(--100vh) - var(--current-stack-height));
  width: 100%;
`;

const FullHeightBoardWrapper = styled.div`
  height: calc(var(--100vh) - var(--current-stack-height));
  width: 100%;
  display: grid;
  position: relative;
`;

const AbsoluteHeader = styled(Vertical)`
  position: absolute;
  width: 100%;
  top: 0;
  @media ${desktopAndAbove} {
    padding-right: var(--triple-unit);
  }
`;

const BottomBarWrapper = styled(Vertical)`
  --bottom-bar-bottom-space: var(--double-unit);
  position: absolute;
  bottom: calc(var(--bottom-bar-height-mobile) + var(--double-unit));
  left: 50%;
  transform: translateX(-50%);
  width: calc(100% - var(--quadruple-unit));

  @media ${laptopAndAbove} {
    --bottom-bar-bottom-space: var(--triple-unit);
    bottom: var(--bottom-bar-bottom-space);
  }

  @media ${desktopAndAbove} {
    bottom: var(--quadruple-unit);
  }
`;

const RightPanel = styled.div`
  display: none;

  @media ${desktopAndAbove} {
    display: flex;
    flex-direction: column;
    position: sticky;
    right: 0;
    top: 0;
    bottom: 0;
    height: 100%;
    overflow-y: auto;
    width: 560px;
    z-index: 2;
    background: rgba(14, 14, 14, 0.19);
    backdrop-filter: blur(16px);
  }
`;

const LegalWarningBannerStyled = styled(LegalWarningBanner)`
  background: var(--c-nd-50-opaque);
`;

type Props = {
  header?: ReactNode;
  children?: ReactNode;
  bottomBar?: ReactNode;
  rightPanel?: ReactNode;
};

export const BoardLayout = ({
  header,
  children,
  bottomBar,
  rightPanel,
}: Props) => {
  return (
    <>
      <Main gap={0}>
        <FullHeightBoardWrapper>
          <AbsoluteHeader>
            <AbsoluteNav />
            {header}
          </AbsoluteHeader>
          {children}
          {!!bottomBar && (
            <BottomBarWrapper center>{bottomBar}</BottomBarWrapper>
          )}
        </FullHeightBoardWrapper>
        {!!rightPanel && <RightPanel>{rightPanel}</RightPanel>}
      </Main>
      <LegalWarningBannerStyled />
    </>
  );
};
